import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

// Step counter hook (unchanged logic you provided)
function useStepCounter(stepLength = 0.75, opts = {}) {
  const {
    thresholdMultiplier = 1.2,
    gravityAlpha = 0.9,
    smoothingAlpha = 0.6,
    bufferSize = 60,
    resumeAllowedRef,
    pausedRef,
  } = opts;

  const [steps, setSteps] = useState(0);
  const [distanceWalked, setDistanceWalked] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const gravityRef = useRef({ x: 0, y: 0, z: 0 });
  const prevFilteredRef = useRef(0);
  const prevPrevRef = useRef(0);
  const bufferRef = useRef([]);
  const lastStepTimeRef = useRef(0);
  const debugRef = useRef({
    rawMag: 0,
    filtered: 0,
    mean: 0,
    std: 0,
    threshold: 0,
  });


  const requestPermission = async () => {
    try {
      if (
        typeof DeviceMotionEvent !== "undefined" &&
        typeof DeviceMotionEvent.requestPermission === "function"
      ) {
        const response = await DeviceMotionEvent.requestPermission();
        if (response === "granted") setPermissionGranted(true);
        else alert("Motion permission denied");
      } else {
        setPermissionGranted(true);
      }
    } catch (err) {
      console.error("Motion permission error:", err);
    }
  };

  useEffect(() => {
    if (!permissionGranted) return;

    function handleMotion(e) {
      const acc = e.accelerationIncludingGravity || e.acceleration;
      if (!acc || typeof acc.x !== "number") return;

      const g = gravityRef.current;
      g.x = gravityAlpha * g.x + (1 - gravityAlpha) * acc.x;
      g.y = gravityAlpha * g.y + (1 - gravityAlpha) * acc.y;
      g.z = gravityAlpha * g.z + (1 - gravityAlpha) * acc.z;

      const linX = acc.x - g.x;
      const linY = acc.y - g.y;
      const linZ = acc.z - g.z;

      const rawMag = Math.sqrt(linX ** 2 + linY ** 2 + linZ ** 2);
      if (rawMag < 0.5) return;

      const filtered =
        smoothingAlpha * prevFilteredRef.current + (1 - smoothingAlpha) * rawMag;

      const buf = bufferRef.current;
      if (buf.length >= bufferSize) buf.shift();
      buf.push(filtered);

      let mean = 0,
        std = 0;
      if (buf.length > 0) {
        mean = buf.reduce((s, v) => s + v, 0) / buf.length;
        const variance =
          buf.reduce((s, v) => s + (v - mean) ** 2, 0) / buf.length;
        std = Math.sqrt(variance);
      }

      const threshold = mean + thresholdMultiplier * std;

      const prev1 = prevFilteredRef.current;
      const prev2 = prevPrevRef.current;
      const now = Date.now();

      const maxAcceleration = 15; // ignore spikes higher than this
      const timeSinceLastStep = now - lastStepTimeRef.current;

      if (
        prev2 < prev1 &&
        prev1 > filtered &&
        prev1 > threshold &&
        timeSinceLastStep > 800 &&
        rawMag < maxAcceleration &&
        (!resumeAllowedRef || resumeAllowedRef.current) &&
        (!pausedRef || !pausedRef.current)
      ) {
        lastStepTimeRef.current = now;
        setSteps((s) => s + 1);
        setDistanceWalked((d) => d + stepLength);
      }

      prevPrevRef.current = prev1;
      prevFilteredRef.current = filtered;

      debugRef.current = { rawMag, filtered, mean, std, threshold };
    }

    window.addEventListener("devicemotion", handleMotion, { passive: true });
    return () => window.removeEventListener("devicemotion", handleMotion);
  }, [
    permissionGranted,
    stepLength,
    gravityAlpha,
    smoothingAlpha,
    bufferSize,
    thresholdMultiplier,
  ]);

  const reset = () => {
    setSteps(0);
    setDistanceWalked(0);
    gravityRef.current = { x: 0, y: 0, z: 0 };
    prevFilteredRef.current = 0;
    prevPrevRef.current = 0;
    bufferRef.current = [];
    lastStepTimeRef.current = 0;
    debugRef.current = { rawMag: 0, filtered: 0, mean: 0, std: 0, threshold: 0 };
  };

  return {
    steps,
    setSteps,
    distanceWalked,
    permissionGranted,
    requestPermission,
    reset,
    debug: debugRef.current,
  };
}

export default function StepNavigatorWithLiveSteps() {
  const [permissionGranted, setPermissionGranted] = useState(false); // compass permission
  const [heading, setHeading] = useState(0);
  const [direction, setDirection] = useState("straight");

  // segment & totals for UI
  const [segmentSteps, setSegmentSteps] = useState(0);
  const [segmentDistance, setSegmentDistance] = useState(0);
  const [totalDistance, setTotalDistance] = useState(0);

  const [segments, setSegments] = useState([]);
  const [finalJson, setFinalJson] = useState("");
  const [location, setLocation] = useState("");
  const [coords, setCoords] = useState({ lat: 0, lng: 0 });
  const [pausedForElevator, setPausedForElevator] = useState(false);


  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [pressed, setPressed] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const pausedRef = useRef(pausedForElevator);


  const stepLength = 0.75;
  const API_URL = "https://api.gokeiro.com/api/routes";
  // const API_URL = "https://gokeiro-backend-d8v9.onrender.com/api/routes"; 
  // const API_URL = "http://192.168.0.105:4000/api/routes"; 


  useEffect(() => {
    pausedRef.current = pausedForElevator;
  }, [pausedForElevator]);

  const {
    steps: totalStepsFromHook,
    distanceWalked,
    permissionGranted: motionGranted,
    requestPermission: requestMotionPermission,
  } = useStepCounter(stepLength, { thresholdMultiplier: 1.6, pausedRef });

  // refs to hold "start of current segment" values
  const lastStepsAtSegmentStart = useRef(0);
  const lastTotalAtSegmentStart = useRef(0);

  const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

  // compass
  useEffect(() => {
    if (!permissionGranted) return;
    const handleOrientation = (event) => {
      // try compass heading then alpha fallback
      let h = event.webkitCompassHeading ?? (typeof event.alpha === "number" ? 360 - event.alpha : null);
      if (h == null) return;
      if (!isNaN(h)) setHeading(normalizeAngle(h));
    };
    window.addEventListener("deviceorientation", handleOrientation, true);
    return () => window.removeEventListener("deviceorientation", handleOrientation);
  }, [permissionGranted]);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (err) => console.warn("geolocation failed", err),
      { enableHighAccuracy: true }
    );
  }, []);

  // update totals when hook changes
  useEffect(() => {
    // total from hook (cumulative)
    setTotalDistance(distanceWalked);

    // segment distance = total - lastTotalAtSegmentStart
    const segDist = +(distanceWalked - lastTotalAtSegmentStart.current).toFixed(2);
    setSegmentDistance(segDist >= 0 ? segDist : 0);

    // segment steps = totalSteps - lastStepsAtSegmentStart
    const segSteps = totalStepsFromHook - lastStepsAtSegmentStart.current;
    setSegmentSteps(segSteps >= 0 ? segSteps : 0);
  }, [distanceWalked, totalStepsFromHook]);

  // permission flow: motion + orientation
  const requestPermissions = async () => {
    await requestMotionPermission();
    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    ) {
      try {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res === "granted") setPermissionGranted(true);
        else alert("Compass permission denied");
      } catch (e) {
        console.warn("orientation permission failed", e);
      }
    } else {
      setPermissionGranted(true);
    }
  };

  // finalize segment: compute relative numbers since last segment start and push
  const finalizeSegment = (type = "walk") => {
    // compute actual segment values from refs
    const segSteps = totalStepsFromHook - lastStepsAtSegmentStart.current;
    const segDistance = +(distanceWalked - lastTotalAtSegmentStart.current).toFixed(2);

    // skip empty walk segments (but allow elevator/destination)
    if (type === "walk" && segSteps <= 0 && segDistance <= 0) return;

    const approx = [normalizeAngle(heading - 10), normalizeAngle(heading + 10)];
    const { minAngle, maxAngle } = getAngleRange(heading);

    const newSegment = {
      type,
      step: segSteps,
      direction,
      distance: +segDistance.toFixed(2),
      totalDistance: +distanceWalked.toFixed(2),
      approxAngle: [
        minAngle,
        maxAngle
      ],
      timestamp: new Date().toISOString(),
    };

    setSegments((p) => [...p, newSegment]);

    // move segment start markers to now
    lastStepsAtSegmentStart.current = totalStepsFromHook;
    lastTotalAtSegmentStart.current = distanceWalked;

    // reset UI segment counters
    setSegmentSteps(0);
    setSegmentDistance(0);
  };

  const getAngleRange = (center, tol = 10) => ({
    minAngle: normalizeAngle(center - tol),
    maxAngle: normalizeAngle(center + tol),
  });

  // when user presses a direction button we finalize the current segment then set new direction
  const handleDirection = (dir) => {

    if (disabled) return; // prevent spam taps

    setPressed(dir);
    setDisabled(true);

    setTimeout(() => {
      setPressed(null);
      setDisabled(false);
    }, 2000); // 2 seconds delay



    // 💥 Add click feedback effect
    const btnEl = document.getElementById(`btn-${dir}`);
    btnEl?.classList.add("clicked");
    setTimeout(() => btnEl?.classList.remove("clicked"), 300);

    if (dir == 'elevator' || dir == 'destination') {
      finalizeSegment("walk");
      if (dir == 'elevator') {
        setPausedForElevator(true);
      }
      setSegments((p) => [...p, {
        type: 'walk',
        step: 0,
        direction: dir,
        distance: 0,
        totalDistance: distanceWalked.toFixed(2),
        approxAngle: [0, 0],
        timestamp: new Date().toISOString(),
      }]);
    } else {
      setPausedForElevator(false);
      if (direction == 'elevator' || direction == 'destination') {
        setDirection(dir);
      } else {
        finalizeSegment("walk");
        setDirection(dir);
      }
    }
  };

  const handleComplete = async () => {
    // finalize any pending walk
    finalizeSegment("walk");

    const routeData = {
      location,
      latLng: `${coords.lat},${coords.lng}`,
      totalSteps: totalStepsFromHook,
      totalDistance: totalDistance.toFixed(2),
      currentHeading: +heading.toFixed(2),
      segments,
    };

    setFinalJson(JSON.stringify(routeData, null, 2));

    try {
      const response = await axios.post(API_URL, routeData);
      console.log("✅ Route saved:", response.data);

      const routeId = response?.data?.routeId || response?.data?.data?.routeId;
      const shareLink = `https://gokeiro.com/${routeId}`;

      setShareUrl(shareLink);
      setShowSharePopup(true);

    } catch (error) {
      console.error("❌ Error saving route:", error);
      alert("Failed to save route");
    }
  };

  if (!motionGranted || !permissionGranted) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h2>📱 Step Navigator (Live)</h2>
        <p>This app needs motion + compass access.</p>
        <button
          onClick={requestPermissions}
          style={{
            background: "rgba(0, 170, 0, 1)",
            color: "white",
            padding: "10px 16px",
            borderRadius: 6,
            border: "none",
          }}
        >
          Start
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, fontFamily: "sans-serif", textAlign: "center" }}>
      <h2>🧭 Step Navigator (Live Steps)</h2>
      <p><b>Heading:</b> {heading.toFixed(1)}°</p>
      <p><b>Direction:</b> {direction.toUpperCase()}</p>
      <p><b>Total Steps:</b> {totalStepsFromHook}</p>
      <p><b>Segment Steps:</b> {segmentSteps}</p>
      <p><b>Segment Distance:</b> {segmentDistance.toFixed(2)} m</p>
      <p><b>Total Distance:</b> {totalDistance.toFixed(2)} m</p>
      <p><b>Location:</b> {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</p>

      <input
        type="text"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        placeholder="Enter location name"
        style={{
          padding: "8px 10px",
          borderRadius: 6,
          border: "1px solid #ccc",
          width: "70%",
          marginBottom: "15px",
        }}
      />

      <div style={buttonGroup}>
        <button disabled={disabled} onClick={() => handleDirection("left")} style={btn("red", pressed === "left", disabled)}>⬅️ Left</button>
        <button disabled={disabled} onClick={() => handleDirection("slight-left")} style={btn("orange", pressed === "slight-left", disabled)}>↖️ Slight-Left</button>
        <button disabled={disabled} onClick={() => handleDirection("straight")} style={btn("green", pressed === "straight", disabled)}>⬆️ Straight</button>
        <button disabled={disabled} onClick={() => handleDirection("slight-right")} style={btn("orange", pressed === "slight-right", disabled)}>↗️ Slight-Right</button>
        <button disabled={disabled} onClick={() => handleDirection("right")} style={btn("blue", pressed === "right", disabled)}>➡️ Right</button>
      </div>

      <div style={buttonGroup}>
        <button disabled={disabled} onClick={() => handleDirection("elevator")} style={btn("#f0ad4e", pressed === "elevator", disabled)}>🛗 Elevator</button>
        <button disabled={disabled} onClick={() => handleDirection("destination")} style={btn("#5cb85c", pressed === "destination",)}>🏁 Destination</button>
      </div>


      <button onClick={handleComplete} style={btn("#222")}>✅ Save Route</button>

      <div style={{ textAlign: "left", marginTop: 20 }}>
        <h4>📄 Segments JSON:</h4>
        <pre
          style={{
            background: "#f7f7f7",
            padding: 10,
            borderRadius: 8,
            maxHeight: 300,
            overflowY: "auto",
          }}
        >
          {finalJson || JSON.stringify(segments.slice(-20), null, 2)}
        </pre>

        {showSharePopup && (
          <div style={popupStyle}>
            <h3>✅ Route Saved!</h3>

            <p style={{ marginBottom: "10px" }}>Share or return later:</p>

            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontWeight: "bold", display: "block", marginBottom: "10px" }}
            >
              {shareUrl}
            </a>

            <button
              onClick={() => navigator.clipboard.writeText(shareUrl)}
              style={copyBtnStyle}
            >
              📋 Copy Link
            </button>

            <button
              onClick={() => window.location.reload()}
              style={closeBtnStyle}
            >
              Close
            </button>
          </div>
        )}

      </div>


    </div>
  );
}


const popupStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "#fff",
  padding: "20px 30px",
  borderRadius: "12px",
  boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
  textAlign: "center",
  zIndex: 9999,
};

const copyBtnStyle = {
  background: "#007bff",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: "6px",
  border: "none",
  marginBottom: "10px",
  cursor: "pointer",
  width: "100%",
};

const closeBtnStyle = {
  background: "#333",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: "6px",
  border: "none",
  cursor: "pointer",
  width: "100%",
};


const buttonGroup = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "10px",
  marginBottom: "15px",
};

const btn = (color, isActive, disabled) => ({
  padding: "12px 16px",
  minWidth: "120px",
  textAlign: "center",
  background: color,
  fontSize: "15px",
  borderRadius: "10px",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  color: "#fff",
  transition: "transform 180ms ease, opacity 180ms ease, background 180ms",
  transform: isActive ? "scale(0.92)" : "scale(1)",
  opacity: disabled ? 0.6 : 1,
  boxShadow: isActive ? "0 0 0 6px rgba(0,0,0,0.1)" : "0 3px 10px rgba(0,0,0,0.25)",
});